import React, { useState, useRef, useEffect } from "react";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { GlobalIcon, LogoIcon } from "@assets/icons";
import P from "@components/common/P/P";
import Button from "@components/common/Button/Button";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/routes";

export const languages = [
	{ value: "tr", label: "Türkçe" },
	{ value: "en", label: "English" },
	{ value: "ru", label: "Русский" },
];

const Navbar: React.FC = () => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const { i18n, t } = useTranslation();
	const navigate = useNavigate();

	const changeLanguage = (lang: string) => {
		i18n.changeLanguage(lang);
		localStorage.setItem("lang", lang);
		setIsDropdownOpen(false);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<nav className="grid grid-cols-maxThree place-content-between items-center px-16 py-4 bg-white border-b border-strokeColor">
			{/* Logo */}
			<div onClick={() => navigate("/")} className="cursor-pointer">
				<SvgIcon Icon={LogoIcon} className="lg:w-[170px] lg:h-[50px]" />
			</div>

			{/* Navigation Links */}
			<div className="grid grid-cols-maxFour gap-6 items-center">
				<P
					size="p3"
					type="regular"
					className="cursor-pointer"
					onClick={() => navigate(ROUTES.HOME)}
				>
					{t("navbar.home")}
				</P>
				<P
					size="p3"
					type="regular"
					className="cursor-pointer"
					onClick={() => navigate(ROUTES.PRODUCTS)}
				>
					{t("navbar.products")}
				</P>
				<P
					size="p3"
					type="regular"
					className="cursor-pointer"
					onClick={() => navigate(ROUTES.ABOUT)}
				>
					{t("navbar.about")}
				</P>
				<P
					size="p3"
					type="regular"
					className="cursor-pointer"
					onClick={() => navigate(ROUTES.FAQ)}
				>
					{t("navbar.faq")}
				</P>
			</div>

			{/* Language Selector & Contact Button */}
			<div className="grid grid-cols-maxTwo gap-2 items-center">
				<div className="relative" ref={dropdownRef}>
					<div
						className="grid grid-cols-maxTwo gap-2 p-2 rounded-lg bg-grayOne cursor-pointer"
						onClick={() => setIsDropdownOpen(!isDropdownOpen)}
					>
						<SvgIcon Icon={GlobalIcon} />
						<P size="p3" type="regular">
							{languages.find((l) => l.value === i18n.language)?.label ||
								"Language"}
						</P>
					</div>

					{isDropdownOpen && (
						<div className="absolute right-0 mt-1 w-32 z-50 bg-white shadow-md rounded border">
							{languages.map((lang) => (
								<button
									key={lang.value}
									className="block w-full text-left px-4 py-2 hover:bg-gray-100"
									onClick={() => changeLanguage(lang.value)}
								>
									{lang.label}
								</button>
							))}
						</div>
					)}
				</div>

				<Button appearance="main">{t("navbar.contact")}</Button>
			</div>
		</nav>
	);
};

export default Navbar;
